import { useState, useMemo, useEffect } from 'react';
export { surveyHandle as handle } from '../routeMetadata';
import { surveyQuestions } from '../data/surveyQuestions';
import { Button } from '../components/Button';
import { TextInput } from '../components/TextInput';
import { TAN_BG } from '../data/constants';
import { useOrder } from '../contexts/OrderContext';

const { URL } = import.meta.env;
const SURVEY_STATE_KEY = 'survey-state';

interface SurveyAnswers {
  [key: string]: string | boolean | string[];
}
interface SurveyOtherInputs {
  [key: string]: string;
}
interface SurveyState {
  answers: SurveyAnswers;
  otherInputs: SurveyOtherInputs;
  email: string;
  currentQuestionIndex: number;
}

const defaultSurvey: SurveyState = {
  answers: {},
  otherInputs: {},
  email: '',
  currentQuestionIndex: 0,
};

function getValidationError(question: typeof surveyQuestions[0], answer: any, otherInput: string) {
  if (!question) return '';
  if (question.type === 'informational') return '';
  if (answer === undefined || answer === null || (Array.isArray(answer) && answer.length === 0)) {
    return 'This question is required.';
  }
  if (question.type === 'choice' && question.promptInputForOther) {
    if ((Array.isArray(answer) && answer.includes('Other') && !otherInput) || (answer === 'Other' && !otherInput)) {
      return 'Please specify for "Other".';
    }
  }
  if (question.type === 'text input' && typeof answer === 'string' && !answer.trim()) {
    return 'Please answer the question';
  }
  return '';
}

function QuestionRenderer({ question, answer, otherInput, onAnswer, onOtherInput, error, onEnter }: any) {

  console.log('error', error);

  if (!question) return null;
  return (
    <div key={question.id} className="flex flex-col items-center justify-center text-center">
      <h2 className="text-lg font-semibold mb-1 text-gray-700">{question.question}</h2>
      {question.subtext && (
        <p className="text-sm text-gray-500 mb-2 text-center">{question.subtext}</p>
      )}
      {question.type === 'informational' && (
        <p className="text-sm text-gray-600 mb-4">This is an informational message. Click "Next" to continue.</p>
      )}
      {question.type === 'boolean' && (
        <div className="space-y-2 mt-4 mb-4">
          <Button onClick={() => onAnswer(true)} variant={answer === true ? 'primary' : 'secondary'} className="w-full">Yes</Button>
          <Button onClick={() => onAnswer(false)} variant={answer === false ? 'primary' : 'secondary'} className="w-full">No</Button>
        </div>
      )}
      {question.type === 'choice' && question.options && (
        <div className="space-y-2 mt-4 mb-4">
          {question.multipleChoice ? (
            question.options.map((option: string) => (
              <label key={option} className="flex items-center space-x-2 w-full cursor-pointer">
                <input
                  type="checkbox"
                  checked={Array.isArray(answer) && answer.includes(option)}
                  onChange={() => onAnswer(option)}
                  className="accent-blue-500"
                />
                <span className="flex-1">{option}</span>
              </label>
            ))
          ) : (
            question.options.map((option: string) => (
              <Button
                key={option}
                onClick={() => onAnswer(option)}
                variant={answer === option ? 'primary' : 'secondary'}
                className="w-full"
              >
                {option}
              </Button>
            ))
          )}
          {question.promptInputForOther && ((Array.isArray(answer) && answer.includes('Other')) || answer === 'Other') && (
            <TextInput
              type="text"
              placeholder="Please specify"
              value={otherInput || ''}
              onChange={e => onOtherInput(e.target.value)}
              className="mt-2"
              onKeyDown={e => { if (e.key === 'Enter' && onEnter) onEnter(); }}
            />
          )}
        </div>
      )}
      {question.type === 'text input' && (
        <TextInput
          type="text"
          placeholder="Type your answer here"
          value={answer || ''}
          onChange={e => onAnswer(e.target.value)}
          className="mt-4 mb-4"
          onKeyDown={e => { if (e.key === 'Enter' && onEnter) onEnter(); }}
        />
      )}
      {error && (
        <div className="text-yellow-700 bg-yellow-100 rounded px-4 py-2 text-center text-sm mt-4 flex flex-col items-center justify-center">
          <span className="text-2xl mb-1">⚠️</span>
          <span>{error}</span>
        </div>
      )}
    </div>
  );
}

function SurveyNavigation({ onPrev, onNext, onSubmit, isSurveyOver, isFirst, isLast, isSubmitting, error }: any) {
  return (
    <div className="mt-8 flex w-full">
      {!isFirst ? (
        <div className="flex-1 flex items-center">
          <Button onClick={onPrev} disabled={isFirst || isSubmitting} variant="secondary">Previous</Button>
        </div>
      ) : (
        <div className="flex-1" />
      )}
      <div className="flex-1 flex justify-end items-center">
        {!isSurveyOver ? (
          <Button onClick={onNext} disabled={isSubmitting}>{isLast ? 'Next (Review & Submit)' : 'Next'}</Button>
        ) : (
          <Button onClick={onSubmit} disabled={isSubmitting} loading={isSubmitting}>Submit Survey</Button>
        )}
      </div>
    </div>
  );
}

function EmailStep({ email, onChange, error }: any) {
  return (
    <div className="flex flex-col items-center justify-center text-center">
      <h2 className="text-lg font-semibold mb-4 text-gray-700">One last step!</h2>
      <p className="text-sm text-gray-600 mb-4">Please provide your email address to receive your discount code and submit the survey.</p>
      <TextInput
        type="email"
        placeholder="Enter your email"
        value={email}
        onChange={e => onChange(e.target.value)}
        className="mb-4"
        required
      />
      {error && <div className="text-red-600 text-sm mt-2">{error}</div>}
    </div>
  );
}

export default function Survey() {
  const [surveyState, setSurveyState] = useState<SurveyState>(() => {
    if (typeof window === 'undefined') return defaultSurvey;
    const cached = localStorage.getItem(SURVEY_STATE_KEY);
    if (cached) {
      const parsed = JSON.parse(cached);
      const currentQuestionIds = surveyQuestions.map(q => q.id);
      const cachedQuestionIds = Object.keys(parsed.answers);
      const isValid = cachedQuestionIds.every(id => currentQuestionIds.includes(id));
      if (isValid) {
        return { ...defaultSurvey, ...parsed };
      }
    }
    return defaultSurvey;
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string>('');
  const { order, updateOrder } = useOrder();

  const applicableQuestions = useMemo(() => surveyQuestions.filter((question) => {
    if (!question.dependsOn) return true;
    const dependency = question.dependsOn;
    const userAnswer = surveyState.answers[dependency.questionId];
    if (Array.isArray(dependency.answer)) {
      if (Array.isArray(userAnswer)) {
        return dependency.answer.some(depAnswer => userAnswer.some(ua => ua === depAnswer));
      } else {
        return dependency.answer.some(depAnswer => userAnswer === depAnswer);
      }
    }
    if (Array.isArray(userAnswer)) {
      return userAnswer.some(ua => ua === dependency.answer);
    }
    return userAnswer === dependency.answer;
  }), [surveyState.answers]);

  useEffect(() => {
    let targetIndex = surveyState.currentQuestionIndex;
    if (applicableQuestions.length > 0) {
      if (targetIndex > applicableQuestions.length) {
        targetIndex = applicableQuestions.length;
      }
    } else {
      targetIndex = 0;
    }
    if (targetIndex !== surveyState.currentQuestionIndex) {
      setSurveyState(prev => ({ ...prev, currentQuestionIndex: targetIndex }));
    }
  }, [applicableQuestions, surveyState.currentQuestionIndex]);

  useEffect(() => {
    localStorage.setItem(SURVEY_STATE_KEY, JSON.stringify(surveyState));
  }, [surveyState]);

  const currentQuestion = applicableQuestions[surveyState.currentQuestionIndex];
  const isSurveyOver = surveyState.currentQuestionIndex >= applicableQuestions.length;
  const isLastQuestion = surveyState.currentQuestionIndex === applicableQuestions.length - 1 && applicableQuestions.length > 0;

  const handleAnswer = (questionId: string, answer: string | boolean) => {
    setSurveyState(prev => {
      const newAnswers = { ...prev.answers };
      // Only update the answer for the current question, don't clear dependent answers here
      if (currentQuestion.type === 'choice' && typeof answer === 'string') {
        const currentAnswer = newAnswers[questionId];
        if (currentQuestion.multipleChoice) {
          if (Array.isArray(currentAnswer)) {
            if (currentAnswer.includes(answer)) {
              newAnswers[questionId] = currentAnswer.filter(a => a !== answer);
            } else {
              newAnswers[questionId] = [...currentAnswer, answer];
            }
          } else {
            newAnswers[questionId] = [answer];
          }
        } else {
          newAnswers[questionId] = answer;
        }
      } else {
        newAnswers[questionId] = answer;
      }
      // Do NOT clear dependent answers here, only when the answer actually changes
      return { ...prev, answers: newAnswers };
    });
    setError('');
  };

  const handleOtherInput = (questionId: string, value: string) => {
    setSurveyState(prev => ({ ...prev, otherInputs: { ...prev.otherInputs, [questionId]: value } }));
    setError('');
  };

  const handleNext = () => {
    const question = currentQuestion;
    if (!question) return;
    const error = getValidationError(question, surveyState.answers[question.id], surveyState.otherInputs[question.id]);
    if (error) {
      setError(error);
      return;
    }
    setError('');
    setSurveyState(prev => ({ ...prev, currentQuestionIndex: prev.currentQuestionIndex + 1 }));
  };

  const handlePrev = () => {
    setSurveyState(prev => ({ ...prev, currentQuestionIndex: prev.currentQuestionIndex - 1 }));
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setError('');
    try {
      const response = await fetch(`/.netlify/functions/submitSurvey`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          answers: surveyState.answers,
          otherInputs: surveyState.otherInputs,
          email: surveyState.email,
          questions: surveyQuestions, // Send the questions as well
        }),
      });
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      const result = await response.json();
      console.log('Survey submitted successfully:', result);
      localStorage.removeItem(SURVEY_STATE_KEY);
      setSurveyState(defaultSurvey);
      updateOrder({ discountCode: result.discountCode });
    } catch (error) {
      setError('Failed to submit survey. Please try again later.');
      console.error('Error submitting survey:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (order.discountCode) {
    return (
      <div className={`flex flex-col items-center justify-center p-4 py-16 ${TAN_BG} text-gray-800`}>
        <h1 className="text-3xl font-bold mb-4">Your Discount Code</h1>
        <p className="text-lg mb-2 text-center">Thank you for completing the survey!</p>
        <p className="text-md mb-8 text-center">
          Your discount code is: <strong className="text-xl">{order.discountCode}</strong>
        </p>
        <p className="text-sm mb-8 text-center">This code has also been saved to your current order.</p>
      </div>
    );
  }

  return (
    <div className="bg-gray-100 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl mx-auto bg-white rounded-lg shadow-md p-6">
        <h1 className="text-2xl font-bold mb-6 text-center">Survey</h1>

        { isSurveyOver
          ? (
              <EmailStep
                email={surveyState.email}
                onChange={(value: string) => setSurveyState(prev => ({ ...prev, email: value }))}
                error={error}
              />
            )
          : (
              <QuestionRenderer
                question={currentQuestion}
                answer={surveyState.answers[currentQuestion?.id]}
                otherInput={surveyState.otherInputs[currentQuestion?.id]}
                onAnswer={(value: string | boolean) => handleAnswer(currentQuestion.id, value)}
                onOtherInput={(value: string) => handleOtherInput(currentQuestion.id, value)}
                error={error}
                onEnter={handleNext}
              />
            )
        }

        <SurveyNavigation
          onPrev={handlePrev}
          onNext={handleNext}
          onSubmit={handleSubmit}
          isSurveyOver={isSurveyOver}
          isFirst={surveyState.currentQuestionIndex === 0}
          isLast={isLastQuestion}
          isSubmitting={isSubmitting}
          error={error}
        />
      </div>
    </div>
  );
}
